// Finestra "Gestisci servizi": lo studio aggiunge, rinomina, ordina ed elimina i servizi dell'area clienti
// (valgono per News, Catalogo, Messaggi e i riquadri dell'app).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ArrowUp, ArrowDown, Trash2, Plus, Check } from 'lucide-react';
import { ICONE, COLORI, stileServizio } from './serviziIcone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/area-clienti-studio`;

function Riga({ s, primo, ultimo, onSposta, onSalva, onElimina }) {
  const [f, setF] = useState(s);
  const [scelta, setScelta] = useState(false);
  useEffect(() => setF(s), [s]);
  const cambiato = f.nome !== s.nome || (f.nome_zh || '') !== (s.nome_zh || '') || f.icona !== s.icona || f.colore !== s.colore || f.riquadro !== s.riquadro;
  const { icona: Icona, colore } = stileServizio(f);
  const usi = s.usi || {};
  return (
    <div className="border rounded-lg p-2 space-y-2">
      <div className="flex items-center gap-2">
        <button type="button" title="Icona e colore" onClick={() => setScelta(!scelta)}
          className={`${colore} w-9 h-9 rounded-lg flex items-center justify-center shrink-0`}><Icona className="w-5 h-5" /></button>
        <Input value={f.nome} maxLength={40} onChange={(e) => setF({ ...f, nome: e.target.value })} className="h-9" />
        <Input value={f.nome_zh || ''} maxLength={40} placeholder="中文 (auto)" title="Nome in cinese: se vuoto si traduce da solo" onChange={(e) => setF({ ...f, nome_zh: e.target.value })} className="h-9 w-32" />
        <label className="flex items-center gap-1 text-xs whitespace-nowrap" title="Compare come riquadro nell'app e può avere il catalogo">
          <input type="checkbox" checked={!!f.riquadro} onChange={(e) => setF({ ...f, riquadro: e.target.checked })} /> riquadro app
        </label>
        {cambiato && <Button size="sm" className="h-8" onClick={() => onSalva(f)}><Check className="w-4 h-4" /></Button>}
        <Button size="sm" variant="ghost" className="h-8 px-1.5" disabled={primo} onClick={() => onSposta(-1)}><ArrowUp className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" className="h-8 px-1.5" disabled={ultimo} onClick={() => onSposta(1)}><ArrowDown className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" className="h-8 px-1.5 text-red-600" onClick={onElimina}><Trash2 className="w-4 h-4" /></Button>
      </div>
      <div className="text-[11px] text-slate-500 pl-11">Usato da {usi.news || 0} news · {usi.catalogo || 0} pratiche · {usi.messaggi || 0} messaggi</div>
      {scelta && (
        <div className="pl-11 space-y-2">
          <div className="flex flex-wrap gap-1">
            {Object.entries(ICONE).map(([k, I]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, icona: k })}
                className={`w-8 h-8 rounded-md flex items-center justify-center border ${f.icona === k ? 'border-slate-900 bg-slate-100' : 'border-slate-200'}`}><I className="w-4 h-4" /></button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(COLORI).map(([k, cls]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, colore: k })}
                className={`${cls} w-8 h-8 rounded-md border-2 ${f.colore === k ? 'border-slate-900' : 'border-transparent'}`} title={k} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function GestioneServizi({ aperta, onChiudi, onCambiato }) {
  const [elenco, setElenco] = useState([]);
  const [nuovo, setNuovo] = useState('');
  const carica = useCallback(async () => {
    try { setElenco((await axios.get(`${API}/servizi`)).data.servizi); } catch (_) { toast.error('Errore nel caricamento dei servizi'); }
  }, []);
  useEffect(() => { if (aperta) carica(); }, [aperta, carica]);
  const fatto = async () => { await carica(); onCambiato && onCambiato(); };

  const salva = async (s) => {
    try { await axios.put(`${API}/servizi/${s.id}`, s); toast.success('Servizio salvato'); fatto(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Salvataggio non riuscito'); }
  };
  const aggiungi = async () => {
    try { await axios.post(`${API}/servizi`, { nome: nuovo, icona: 'briefcase', colore: 'slate', riquadro: false }); setNuovo(''); toast.success('Servizio aggiunto'); fatto(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non aggiunto'); }
  };
  const elimina = async (s) => {
    if (!window.confirm(`Eliminare il servizio "${s.nome}"?`)) return;
    try { await axios.delete(`${API}/servizi/${s.id}`); toast.success('Servizio eliminato'); fatto(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Non eliminato'); }
  };
  const sposta = async (i, verso) => {
    const a = elenco[i]; const b = elenco[i + verso];
    if (!b) return;
    try {
      await axios.put(`${API}/servizi/${a.id}`, { ...a, ordine: b.ordine });
      await axios.put(`${API}/servizi/${b.id}`, { ...b, ordine: a.ordine === b.ordine ? a.ordine + verso : a.ordine });
      fatto();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  return (
    <Dialog open={aperta} onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gestisci servizi (area clienti)</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Valgono per News, Catalogo, Messaggi e i riquadri dell'app. Clic sull'icona per cambiarla. Rinominando un servizio, news e pratiche lo seguono.</p>
        <div className="space-y-2">
          {elenco.map((s, i) => (
            <Riga key={s.id} s={s} primo={i === 0} ultimo={i === elenco.length - 1}
              onSposta={(v) => sposta(i, v)} onSalva={salva} onElimina={() => elimina(s)} />
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t">
          <Input value={nuovo} maxLength={40} placeholder="Nuovo servizio (es. Cittadinanza)" onChange={(e) => setNuovo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && nuovo.trim().length >= 2) aggiungi(); }} />
          <Button onClick={aggiungi} disabled={nuovo.trim().length < 2}><Plus className="w-4 h-4 mr-1" /> Aggiungi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
